/*
 * @author: yanhoup
 * @description: 时间格式化
 */
import moment from 'moment';
import momentTz from 'moment-timezone';

interface IRelativeDate {
  relativeDays: string;
  time: string;
}

export const sysTimeZone = 'Asia/Shanghai';
export const getLocalTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * @description 时间格式化
 * @param date  必填
 * @param format 选填
 * @returns string
 */
const formatDate = (date: string | number, format = 'YYYY-MM-DD HH:mm:ss'): string => {
  return date && date !== 1000 && date !== '1970-01-01T00:00:01.000+00:00' ? moment(date).format(format) : '';
};

/**
 * @description 系统相对时间 和 本地相对日期 互相转换
 * @param relativeDateTime 需要转换的相对时间 '0 12:00'
 * @param fromTimeZone 来源时区
 * @param toTimeZone 目标时区
 * @returns 目标相对时间 { relativeDays: '0', time: '12:00' }
 */
const convertRelativeDate = (relativeDateTime: string, fromTimeZone: string, toTimeZone: string) => {
  if (!relativeDateTime) {
    return [];
  }
  const [relativeDays, time] = relativeDateTime.split(' ');
  if (fromTimeZone === toTimeZone || !(relativeDays && time)) {
    return {
      relativeDays,
      time,
    };
  }
  // 1. 构建来源时区的绝对时间
  const fromDate = momentTz().tz(fromTimeZone).add(relativeDays, 'days').format('YYYY-MM-DD');
  const fromDateTime = momentTz.tz(`${fromDate} ${time}`, fromTimeZone);
  // 2. 转换为目标时区的绝对时间
  const toDateTime = fromDateTime.clone().tz(toTimeZone);
  const toDateTimeStartOfDay = toDateTime.clone().startOf('day');
  // 3. 计算本地时区相对于今天的天数差
  const toToday = momentTz().tz(toTimeZone).startOf('day');
  const toRelativeDays = toDateTimeStartOfDay.diff(toToday, 'days')?.toString();
  // 4. 获取本地时区的时间
  const toTime = toDateTime.format('HH:mm');

  return {
    relativeDays: toRelativeDays,
    time: toTime,
  };
};

/**
 * @description 将相对日期范围从一个时区转换为另一个时区
 * @param rangeDate 相对日期范围数组 ['-14 20:00', '0 20:00']
 * @param fromTimeZone 来源时区，默认为本地时区
 * @param toTimeZone 目标时区，默认为东八区（Asia/Shanghai）
 * @returns 转换后的相对日期范围数组 ['-14 21:00', '0 21:00']
 */
const convertRelativeRangeDate = (
  rangeDate: string[],
  fromTimeZone: string = getLocalTimeZone(),
  toTimeZone: string = sysTimeZone,
): string[] => {
  if (!rangeDate || rangeDate.length !== 2) {
    return rangeDate;
  }

  const [startDateTime, endDateTime] = rangeDate;

  // 如果来源时区和目标时区相同，直接返回
  if (fromTimeZone === toTimeZone) {
    return rangeDate;
  }

  // 转换开始时间
  const { relativeDays: startDays, time: startTime } = convertRelativeDate(
    startDateTime,
    fromTimeZone,
    toTimeZone,
  ) as IRelativeDate;
  const startConverted = `${startDays} ${startTime}`;

  // 转换结束时间
  const { relativeDays: endDays, time: endTime } = convertRelativeDate(
    endDateTime,
    fromTimeZone,
    toTimeZone,
  ) as IRelativeDate;
  const endConverted = `${endDays} ${endTime}`;

  return [startConverted, endConverted];
};

/**
 * @description 将单个时间从一个时区转换为另一个时区
 * @param time 时间字符串 '01:00'
 * @param fromTimeZone 来源时区，默认为本地时区
 * @param toTimeZone 目标时区，默认为东八区（Asia/Shanghai）
 * @returns 转换后的时间字符串 '00:00'
 */
const convertRelativeTime = (
  time: string,
  fromTimeZone: string = getLocalTimeZone(),
  toTimeZone: string = sysTimeZone,
): string => {
  if (!time) {
    return time;
  }

  // 如果来源时区和目标时区相同，直接返回
  if (fromTimeZone === toTimeZone) {
    return time;
  }

  // 1. 构建来源时区的绝对时间（使用今天的日期）
  const fromDate = momentTz.tz(fromTimeZone).format('YYYY-MM-DD');
  const fromDateTime = momentTz.tz(`${fromDate} ${time}`, fromTimeZone);
  // 2. 转换为目标时区的绝对时间
  const toDateTime = fromDateTime.clone().tz(toTimeZone);
  // 3. 获取目标时区的时间
  const toTime = toDateTime.format('HH:mm');

  return toTime;
};

/**
 * @description 将时间范围从一个时区转换为另一个时区
 * @param rangeTime 时间范围数组 ['01:00', '02:00']
 * @param fromTimeZone 来源时区，默认为本地时区
 * @param toTimeZone 目标时区，默认为东八区（Asia/Shanghai）
 * @returns 转换后的时间范围数组 ['00:00', '01:00']
 */
const convertRelativeRangeTime = (
  rangeTime: string[],
  fromTimeZone: string = getLocalTimeZone(),
  toTimeZone: string = sysTimeZone,
): string[] => {
  if (!rangeTime || rangeTime.length !== 2) {
    return rangeTime;
  }

  const [startTime, endTime] = rangeTime;

  // 如果来源时区和目标时区相同，直接返回
  if (fromTimeZone === toTimeZone) {
    return rangeTime;
  }

  return [
    convertRelativeTime(startTime, fromTimeZone, toTimeZone),
    convertRelativeTime(endTime, fromTimeZone, toTimeZone),
  ];
};
// 生成UUID 唯一ID
const getuuId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export {
  formatDate,
  convertRelativeDate,
  convertRelativeTime,
  convertRelativeRangeTime,
  convertRelativeRangeDate,
  getuuId,
};
